import { MouseEvent, useEffect } from "react";
import { Trans } from "react-i18next";

import { NavItem } from "@/components/PortalNav/MobileNav";
import PortalNav from "@/components/PortalNav/PortalNav";
import ProfileMenuItem from "@/components/PortalNav/ProfileMenuItem";
import useParentsProps from "@/hooks/useParentsProps.ts";
import useSettingsPageProps from "@/hooks/useSettingsPageProps.ts";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { router, useForm } from "@inertiajs/react";

import { MenuSeparator } from "@chakra-ui/react";

const ParentsPortalNav = () => {
    const { post } = useForm({});
    const { parentUser } = useParentsProps();
    const { parentsNav = [] } = useSettingsPageProps();

    // Set up Datadog tracking
    useEffect(() => {
        if (parentUser) {
            const studentAuth0Usernames = parentUser.students
                ?.map(student => student.auth0_username)
                .join(", ");
            datadogRum.setUser({
                id: parentUser.id.toString(),
                name: parentUser.name,
                chargebeeId: parentUser.chargebeeId,
                students: studentAuth0Usernames
            });
            datadogLogs.setUser({
                id: parentUser.id.toString(),
                name: parentUser.name,
                chargebeeId: parentUser.chargebeeId,
                students: studentAuth0Usernames
            });
        }
    }, [parentUser]);

    const handleLogout = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        post(route("logout"), {
            replace: true
        });
    };

    const navItems: NavItem[] = parentsNav.map(item => ({
        id: item.id,
        name: item.name,
        route: route(item.route),
        isExternal: item.isExternal
    }));

    const profileMenuItems = (
        <>
            <ProfileMenuItem
                value="parents.account-details"
                data-testid="accountDetails"
                onClick={() => router.get(route("parents.account-details"))}
            >
                <Trans>Account details</Trans>
            </ProfileMenuItem>
            <ProfileMenuItem
                value="parents.shared-access"
                data-testid="sharedAccess"
                onClick={() => router.get(route("parents.shared-access"))}
            >
                <Trans>Shared access</Trans>
            </ProfileMenuItem>
            <MenuSeparator marginInline={4} />
            <ProfileMenuItem
                value="logout"
                data-testid="logout"
                color="base.800"
                onClick={e => handleLogout(e)}
            >
                <Trans>Logout</Trans>
            </ProfileMenuItem>
        </>
    );

    // render only the logo if user is not authenticated
    if (!parentUser || parentUser.id === 0) {
        return (
            <PortalNav
                logoHref={route("parents.dashboard")}
                backgroundColor="base.300"
                showBottomBorder
            />
        );
    }

    return (
        <PortalNav
            logoHref={route("parents.dashboard")}
            navItems={navItems}
            userDisplayName={parentUser.displayName}
            profileMenuItems={profileMenuItems}
            showProfile
            backgroundColor="base.300"
            showBottomBorder
        />
    );
};

export default ParentsPortalNav;
